import { z } from "zod";

// ADR-006 §4: an override names a flag, the organization it governs (absent for the whole
// platform), whether it is on, who set it and why, and when. The key itself stays a plain
// string here: a contract may depend on zod and @amtana/module alone, so the catalogue's
// typed keys (`@amtana/flags`, a framework package) are checked where they are read, in the
// process half and at the door; the wire only asks for a non-empty string.

export const flagKeySchema = z.string().min(1);

export const isEnabledInputSchema = z.object({
  key: flagKeySchema,
  organizationId: z.string().optional(),
});
export type IsEnabledInput = z.infer<typeof isEnabledInputSchema>;

export const resolveAllInputSchema = z.object({ organizationId: z.string() });
export type ResolveAllInput = z.infer<typeof resolveAllInputSchema>;

export const resolveAllOutputSchema = z.object({ flags: z.record(z.string(), z.boolean()) });
export type ResolveAllOutput = z.infer<typeof resolveAllOutputSchema>;

export const flagOverrideSchema = z.object({
  key: flagKeySchema,
  /** Absent means the whole platform; present scopes the override to one organization. */
  organizationId: z.string().nullable(),
  enabled: z.boolean(),
  setBy: z.string(),
  reason: z.string().min(1),
  setAt: z.string(),
});
export type FlagOverride = z.infer<typeof flagOverrideSchema>;

export const setOverrideInputSchema = z.object({
  key: flagKeySchema,
  organizationId: z.string().nullable(),
  enabled: z.boolean(),
  setBy: z.string(),
  reason: z.string().min(1, "a reason is required: every override says why, for the audit (ADR-006 §8)."),
});
export type SetOverrideInput = z.infer<typeof setOverrideInputSchema>;

export const clearOverrideInputSchema = z.object({
  key: flagKeySchema,
  organizationId: z.string().nullable(),
});
export type ClearOverrideInput = z.infer<typeof clearOverrideInputSchema>;

// S8.06: the back office's own read (ops calls `overview` as a peer; no transport door names
// it). One entry per catalogued flag, its platform override and every organization override
// that differs from it; `orphans` lists a stored override naming a key the catalogue no
// longer has (ADR-006 §9's catalogue test fails while one is stored).
export const flagOverviewEntrySchema = z.object({
  key: flagKeySchema,
  scope: z.enum(["organization", "platform"]),
  default: z.boolean(),
  description: z.string(),
  owningModule: z.string(),
  platformOverride: flagOverrideSchema.nullable(),
  organizationOverrides: z.array(flagOverrideSchema),
});
export type FlagOverviewEntry = z.infer<typeof flagOverviewEntrySchema>;

export const flagsOverviewOutputSchema = z.object({
  flags: z.array(flagOverviewEntrySchema),
  orphans: z.array(flagOverrideSchema),
});
export type FlagsOverviewOutput = z.infer<typeof flagsOverviewOutputSchema>;
